import React, { useState, useMemo } from 'react';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../BootstrapExtends/index.js';
import Column from '../../../models/SpreadsheetReportConfigs/Column.js';
import PropTypes from 'prop-types';
import Box from '../../atoms/Box.jsx';
import { GenericDndContainer } from '../../molecules/GenericDnd';
import OutputColumnDNDItem from './OutputColumnDNDItem/index.jsx';
import {
  OTHER_COLUMN_VALUES,
  OUTPUT_COLUMN_TABS,
  OVER_PERIOD_THRESHOLD_DAYS_DISABLED_AGGREGATION_AXIS_COLUMNS,
  getConflictingAggregationAxisColumns,
  DND_ITEM_TYPES,
  getAggregationAxisColumnGroupKey,
  KEYWORD_METRIC_CONFLICT_METRICS,
  KEYWORD_METRIC_CONFLICT_COLUMNS,
} from '../../../constants/SpreadsheetReportConfigs.js';
import { generateUniqueId } from '../../../lib/StringUtils.js';
import {
  AggregationAxisColumn,
  AdvertisingTitleColumn,
  ColumnToTabMap,
  OtherColumn,
} from '../../../enums/SpreadsheetReportConfigs.js';
import ConversionPoint from '../../../models/SpreadsheetReportConfigs/ConversionPoint';
import { reorderItem } from '../../../lib/DndUtils.js';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm.sheetConfigSection.panelSection.outputFieldSection';

const TAB_TITLE_MAP = {
  [OUTPUT_COLUMN_TABS.aggregationAxis]: `${scope}.tabs.aggregationAxis`,
  [OUTPUT_COLUMN_TABS.advertisingTitle]: `${scope}.tabs.title`,
  [OUTPUT_COLUMN_TABS.other]: `${scope}.tabs.other`,
};

const propTypes = {
  isOverPeriodThreshold: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape(Column.propTypes)),
  onChange: PropTypes.func,
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  filters: PropTypes.array,
  highlightedColumnContents: PropTypes.arrayOf(PropTypes.string),
};

const columnListPropTypes = {
  columns: PropTypes.object.isRequired,
  conversionPointsColumns: PropTypes.object,
  showConversionPoints: PropTypes.bool,
  disabledColumns: PropTypes.arrayOf(PropTypes.string),
  checkedColumns: PropTypes.arrayOf(PropTypes.shape(Column.propTypes)),
  onColumnChange: PropTypes.func.isRequired,
};

const ColumnList = ({
  columns,
  conversionPointsColumns,
  showConversionPoints,
  disabledColumns,
  checkedColumns,
  onColumnChange,
}) => (
  <BS.Panel
    style={{
      borderTop: 'none',
      borderRadius: '0',
      overflowY: 'auto',
      maxHeight: '300px',
    }}
  >
    <BS.Panel.Body>
      {Object.entries(columns).map(([key, option]) => {
        const isColumnDisabled =
          disabledColumns &&
          disabledColumns.includes(option.value) &&
          !checkedColumns.some((column) => column.content === option.value);

        return (
          <BS.Checkbox
            key={key}
            value={option.value}
            className={`spreadsheet-report-config-option${
              isColumnDisabled
                ? ' spreadsheet-report-config-option--disabled'
                : ''
            }`}
            disabled={isColumnDisabled}
            checked={checkedColumns.some(
              (column) => column.content === option.value
            )}
            onChange={onColumnChange(option)}
          >
            {option.label}
          </BS.Checkbox>
        );
      })}
      {showConversionPoints &&
        Object.entries(conversionPointsColumns).map(([key, option]) => (
          <BS.Checkbox
            key={`${key}-conversion-point`}
            value={option.value}
            className='spreadsheet-report-config-option'
            checked={checkedColumns.some(
              (column) => column.content === option.value
            )}
            onChange={onColumnChange(option)}
          >
            {option.label}
          </BS.Checkbox>
        ))}
    </BS.Panel.Body>
  </BS.Panel>
);

const OutputColumnSelect = ({
  isOverPeriodThreshold,
  columns,
  onChange,
  conversionPoints,
  filters = [],
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedTab, setSelectedTab] = useState(
    OUTPUT_COLUMN_TABS.aggregationAxis
  );

  const conversionPointsObject = useMemo(() => {
    return conversionPoints.reduce((acc, point) => {
      acc[point.key] = point;
      return acc;
    }, {});
  }, [conversionPoints]);

  const baseColumnLabelMap = useMemo(() => {
    const map = {};
    Object.values(AggregationAxisColumn).forEach((col) => {
      map[col.value] = col.label;
    });
    Object.values(AdvertisingTitleColumn).forEach((col) => {
      map[col.value] = col.label;
    });
    Object.values(OtherColumn).forEach((col) => {
      map[col.value] = col.label;
    });
    return map;
  }, []);

  const conversionPointLabelMap = useMemo(() => {
    return conversionPoints.reduce((acc, point) => {
      acc[point.value] = point.label;
      return acc;
    }, {});
  }, [conversionPoints]);

  const originalColumnLabelMap = useMemo(
    () => ({
      ...baseColumnLabelMap,
      ...conversionPointLabelMap,
    }),
    [baseColumnLabelMap, conversionPointLabelMap]
  );

  const getTabColumns = (tab) => {
    switch (tab) {
      case OUTPUT_COLUMN_TABS.aggregationAxis:
        return AggregationAxisColumn;
      case OUTPUT_COLUMN_TABS.advertisingTitle:
        return AdvertisingTitleColumn;
      default:
        return {};
    }
  };

  const getTabTitleByColumnContent = (columnContent) => {
    const tabKey = ColumnToTabMap[columnContent];
    return tabKey
      ? I18n.t(TAB_TITLE_MAP[tabKey])
      : conversionPoints.some(
          (conversionPoint) => conversionPoint.value === columnContent
        )
      ? I18n.t(`${scope}.tabs.title`)
      : null;
  };

  const filterColumns = (columns, searchValue) => {
    if (!searchValue) return columns;

    const searchLower = searchValue.toLowerCase();
    return Object.fromEntries(
      Object.entries(columns).filter(([, option]) =>
        option.label.toLowerCase().includes(searchLower)
      )
    );
  };

  const displayedColumns = useMemo(() => {
    const tabColumns = getTabColumns(selectedTab);
    return filterColumns(tabColumns, searchValue);
  }, [selectedTab, searchValue]);

  const keywordRelatedMetricSelected = useMemo(() => {
    const keywordMetricValues = new Set([
      AdvertisingTitleColumn.qualityScore.value,
      AdvertisingTitleColumn.exactMatchImpressionShare.value,
      AdvertisingTitleColumn.impressionShare.value,
      AdvertisingTitleColumn.absoluteTopImpressionPercentage.value,
      AdvertisingTitleColumn.topImpressionPercentage.value,
    ]);
    return columns.some((column) => keywordMetricValues.has(column.content));
  }, [columns]);

  const selectedColumnValues = useMemo(() => {
    const values = new Set(columns.map((column) => column.content));
    filters.forEach((andGroup) => {
      if (!Array.isArray(andGroup)) return;
      andGroup.forEach((condition) => {
        if (condition?.targetColumn) values.add(condition.targetColumn);
      });
    });
    return values;
  }, [columns, filters]);

  const keywordMetricSelected = useMemo(() => {
    return KEYWORD_METRIC_CONFLICT_METRICS.some((value) =>
      selectedColumnValues.has(value)
    );
  }, [selectedColumnValues]);

  const keywordConflictColumnSelected = useMemo(() => {
    return KEYWORD_METRIC_CONFLICT_COLUMNS.some((value) =>
      selectedColumnValues.has(value)
    );
  }, [selectedColumnValues]);

  const disabledAggregationAxisColumnsBySelection = useMemo(() => {
    const keywordIdValue = AggregationAxisColumn.keywordRemoteId?.value;
    const virtualGroupKeys =
      keywordRelatedMetricSelected && keywordIdValue
        ? [getAggregationAxisColumnGroupKey(keywordIdValue)].filter(Boolean)
        : [];
    return getConflictingAggregationAxisColumns({
      columns,
      filters,
      virtualGroupKeys,
    });
  }, [columns, filters, keywordRelatedMetricSelected]);

  const disabledAdvertisingTitleColumnsBySelection = useMemo(() => {
    const advertisingTitleValues = new Set(
      Object.values(AdvertisingTitleColumn).map((column) => column.value)
    );
    return disabledAggregationAxisColumnsBySelection.filter((columnValue) =>
      advertisingTitleValues.has(columnValue)
    );
  }, [disabledAggregationAxisColumnsBySelection]);

  const disabledAggregationAxisColumnsByPeriod = useMemo(() => {
    return isOverPeriodThreshold
      ? OVER_PERIOD_THRESHOLD_DAYS_DISABLED_AGGREGATION_AXIS_COLUMNS
      : [];
  }, [isOverPeriodThreshold]);

  const disabledAggregationAxisColumns = useMemo(() => {
    const disabledSet = new Set(disabledAggregationAxisColumnsByPeriod);
    disabledAggregationAxisColumnsBySelection.forEach((columnValue) => {
      disabledSet.add(columnValue);
    });
    if (keywordMetricSelected) {
      KEYWORD_METRIC_CONFLICT_COLUMNS.forEach((columnValue) => {
        disabledSet.add(columnValue);
      });
    }
    return Array.from(disabledSet);
  }, [
    disabledAggregationAxisColumnsBySelection,
    disabledAggregationAxisColumnsByPeriod,
    keywordMetricSelected,
  ]);

  const disabledAdvertisingTitleColumns = useMemo(() => {
    const keywordIdValue = AggregationAxisColumn.keywordRemoteId?.value;
    const keywordIdDisabled =
      keywordIdValue && disabledAggregationAxisColumns.includes(keywordIdValue);
    const disabledSet = new Set(disabledAdvertisingTitleColumnsBySelection);

    if (keywordIdDisabled) {
      [
        AdvertisingTitleColumn.qualityScore.value,
        AdvertisingTitleColumn.exactMatchImpressionShare.value,
        AdvertisingTitleColumn.impressionShare.value,
        AdvertisingTitleColumn.absoluteTopImpressionPercentage.value,
        AdvertisingTitleColumn.topImpressionPercentage.value,
      ].forEach((value) => disabledSet.add(value));
    }
    if (keywordConflictColumnSelected) {
      KEYWORD_METRIC_CONFLICT_METRICS.forEach((value) => {
        disabledSet.add(value);
      });
    }

    return Array.from(disabledSet);
  }, [
    disabledAggregationAxisColumns,
    disabledAdvertisingTitleColumnsBySelection,
    keywordConflictColumnSelected,
  ]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleColumnChange = (option) => () => {
    if (
      columns.some(
        (column) =>
          column.content !== OTHER_COLUMN_VALUES.blank &&
          column.content === option.value
      )
    ) {
      onChange(columns.filter((column) => column.content !== option.value));
    } else {
      onChange([
        ...columns,
        { id: generateUniqueId(), content: option.value, name: option.label },
      ]);
    }
  };

  const moveColumn = (dragIndex, hoverIndex) => {
    // Don't do anything if dropping in the same position
    if (dragIndex === hoverIndex) return;

    const newColumns = reorderItem({ list: columns, dragIndex, hoverIndex });
    onChange(newColumns);
  };

  const handleDelete = (id) => {
    onChange(columns.filter((column) => column.id !== id));
  };

  const handleUpdateName = (id, newName) => {
    onChange(
      columns.map((column) =>
        column.id === id ? { ...column, name: newName } : column
      )
    );
  };

  const renderColumnItem = ({ item: column, index }) => (
    <OutputColumnDNDItem
      key={column.id}
      groupName={getTabTitleByColumnContent(column.content)}
      column={column}
      index={index}
      onDelete={handleDelete}
      onUpdateName={handleUpdateName}
      originalLabel={originalColumnLabelMap[column.content]}
    />
  );

  return (
    <>
      <h4>{I18n.t(`${scope}.sectionTitle`)}</h4>
      <Box>
        <Box.Body>
          <BS.Row>
            <BS.Col sm={6}>
              <GenericDndContainer
                items={columns}
                itemType={DND_ITEM_TYPES.column}
                renderItem={renderColumnItem}
                onMove={moveColumn}
                classNames={{
                  container: 'output-column-dnd-container',
                  itemWrapper: 'output-column-dnd-item-wrapper',
                  dropZone: 'output-column-dnd-drop-zone',
                }}
              />
            </BS.Col>
            <BS.Col sm={6}>
              <BSEx.FormGroupInline
                label={I18n.t(`${scope}.field.search.label`)}
              >
                <BS.FormControl
                  type='text'
                  placeholder={I18n.t(`${scope}.field.search.placeholder`)}
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </BSEx.FormGroupInline>

              <BS.Row>
                <BS.Col smOffset={2} sm={10}>
                  <BS.Tabs
                    id='output-column-tabs'
                    animation={false}
                    activeKey={selectedTab}
                    onSelect={handleTabChange}
                  >
                    <BS.Tab
                      eventKey={OUTPUT_COLUMN_TABS.aggregationAxis}
                      title={I18n.t(`${scope}.tabs.aggregationAxis`)}
                    >
                      <ColumnList
                        columns={displayedColumns}
                        disabledColumns={disabledAggregationAxisColumns}
                        checkedColumns={columns}
                        onColumnChange={handleColumnChange}
                      />
                    </BS.Tab>
                    <BS.Tab
                      eventKey={OUTPUT_COLUMN_TABS.advertisingTitle}
                      title={I18n.t(`${scope}.tabs.title`)}
                    >
                      <ColumnList
                        columns={displayedColumns}
                        disabledColumns={disabledAdvertisingTitleColumns}
                        showConversionPoints={true}
                        conversionPointsColumns={conversionPointsObject}
                        checkedColumns={columns}
                        onColumnChange={handleColumnChange}
                      />
                    </BS.Tab>
                    <BS.Tab
                      eventKey={OUTPUT_COLUMN_TABS.other}
                      title={I18n.t(`${scope}.tabs.other`)}
                    >
                      <BS.Panel
                        style={{
                          borderTop: 'none',
                          borderRadius: '0',
                          overflowY: 'auto',
                          maxHeight: '300px',
                        }}
                      >
                        <BS.Panel.Body>
                          <BS.Button
                            type='button'
                            onClick={handleColumnChange(OtherColumn.blank)}
                          >
                            {OtherColumn.blank.label}
                            <i
                              className='fa fa-plus'
                              style={{ marginLeft: '4px' }}
                            />
                          </BS.Button>
                        </BS.Panel.Body>
                      </BS.Panel>
                    </BS.Tab>
                  </BS.Tabs>
                </BS.Col>
              </BS.Row>
            </BS.Col>
          </BS.Row>
        </Box.Body>
      </Box>
    </>
  );
};

ColumnList.propTypes = columnListPropTypes;
OutputColumnSelect.propTypes = propTypes;

export default OutputColumnSelect;
